import { METRICS_PROFILE } from '../../../shared/constants';

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const randomBetween = (min, max) => min + Math.random() * (max - min);

const jitter = (value, amplitude) => value + randomBetween(-amplitude, amplitude);

export const getMetricsProfile = (edgeControllerId) => {
  if (edgeControllerId === 'EC-HW-2024-002-B') {
    return METRICS_PROFILE.CRITICAL;
  }
  return METRICS_PROFILE.NORMAL;
};

const getMetricRanges = (profile) => {
  if (profile === METRICS_PROFILE.CRITICAL) {
    return {
      cpu: { min: 80, max: 100, drift: 3.5 },
      ram: { min: 75, max: 100, drift: 3.0 },
      diskAvailable: { min: 1, max: 18, drift: 0.6 },
      temperature: { min: 70, max: 95, drift: 1.2 },
    };
  }

  return {
    cpu: { min: 5, max: 35, drift: 2.0 },
    ram: { min: 15, max: 55, drift: 2.0 },
    diskAvailable: { min: 45, max: 95, drift: 0.4 },
    temperature: { min: 35, max: 65, drift: 0.8 },
  };
};

const nextValue = ({ previous, min, max, drift }) => {
  const centerPull = (min + max) / 2;
  const towardCenter = (centerPull - previous) * 0.06;
  const next = jitter(previous + towardCenter, drift);
  return clamp(Math.round(next * 10) / 10, min, max);
};

export const generateHistoricalSeries = ({ profile, startMs, endMs, stepMs }) => {
  const ranges = getMetricRanges(profile);

  let cpu = randomBetween(ranges.cpu.min, ranges.cpu.max);
  let ramValue = randomBetween(ranges.ram.min, ranges.ram.max);
  let diskAvailable = randomBetween(ranges.diskAvailable.min, ranges.diskAvailable.max);
  let temperature = randomBetween(ranges.temperature.min, ranges.temperature.max);

  const cpuSeries = [];
  const ramSeries = [];
  const diskSeries = [];
  const tempSeries = [];

  for (let timestamp = startMs; timestamp <= endMs; timestamp += stepMs) {
    cpu = nextValue({ previous: cpu, ...ranges.cpu });
    ramValue = nextValue({ previous: ramValue, ...ranges.ram });

    const diskTrend = profile === METRICS_PROFILE.CRITICAL ? -randomBetween(0, 0.08) : randomBetween(-0.02, 0.02);
    diskAvailable = nextValue({ previous: diskAvailable + diskTrend, ...ranges.diskAvailable });

    temperature = nextValue({ previous: temperature, ...ranges.temperature });

    cpuSeries.push([timestamp, cpu]);
    ramSeries.push([timestamp, ramValue]);
    diskSeries.push([timestamp, diskAvailable]);
    tempSeries.push([timestamp, temperature]);
  }

  return { cpuSeries, ramSeries, diskSeries, tempSeries };
};

export const generateRecentSeries = ({ profile, endMs, windowMs, stepMs }) => {
  const startMs = endMs - windowMs;
  return generateHistoricalSeries({ profile, startMs, endMs, stepMs });
};

export const appendRealtimePoint = ({ profile, previous }) => {
  const ranges = getMetricRanges(profile);

  const cpu = nextValue({ previous: previous.cpu, ...ranges.cpu });
  const ramValue = nextValue({ previous: previous.ram, ...ranges.ram });

  const diskTrend = profile === METRICS_PROFILE.CRITICAL ? -randomBetween(0, 0.15) : randomBetween(-0.03, 0.03);
  const diskAvailable = nextValue({ previous: previous.diskAvailable + diskTrend, ...ranges.diskAvailable });

  const temperature = nextValue({ previous: previous.temperature, ...ranges.temperature });

  return { cpu, ram: ramValue, diskAvailable, temperature };
};
